import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import EmptyState from './EmptyState.tsx';

// --- Types ---

interface Column<T> {
  key: string;
  header: string | React.ReactNode;
  render: (item: T, index: number) => React.ReactNode;
  width?: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  headerClassName?: string;
  cellClassName?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  loading?: boolean;
  emptyIcon?: LucideIcon;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: { label: string; onClick: () => void; icon?: LucideIcon };
  emptyVariant?: 'default' | 'dashed';
  onRowClick?: (item: T) => void;
  rowClassName?: (item: T, index: number) => string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  virtualized?: boolean;
  rowHeight?: number;
  overscan?: number;
  className?: string;
  headerClassName?: string;
  stickyHeader?: boolean;
}

// --- Alignment helper ---

const alignClass = (align?: 'left' | 'center' | 'right') => {
  if (align === 'center') return 'text-center justify-center';
  if (align === 'right') return 'text-right justify-end';
  return 'text-left';
};

// --- Sort icon ---

const SortIcon: React.FC<{
  column: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}> = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column)
    return (
      <ArrowUpDown className="w-3 h-3 opacity-0 group-hover/sort:opacity-40 transition-opacity" />
    );
  const Icon = sortDirection === 'asc' ? ArrowUp : ArrowDown;
  return <Icon className="w-3 h-3 text-pt-gold" />;
};

// --- Virtualized body ---

const VirtualizedBody = <T,>({
  data,
  columns,
  keyExtractor,
  rowHeight,
  overscan,
  onRowClick,
  rowClassName,
  gridTemplate,
}: {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string;
  rowHeight: number;
  overscan: number;
  onRowClick?: (item: T) => void;
  rowClassName?: (item: T, index: number) => string;
  gridTemplate: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(600);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setContainerHeight(entries[0].contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    if (containerRef.current) setScrollTop(containerRef.current.scrollTop);
  }, []);

  const totalHeight = data.length * rowHeight;
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const endIndex = Math.min(
    data.length,
    Math.ceil((scrollTop + containerHeight) / rowHeight) + overscan
  );
  const visibleItems = data.slice(startIndex, endIndex);

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto" onScroll={handleScroll}>
      <div style={{ height: totalHeight, position: 'relative' }}>
        {visibleItems.map((item, vi) => {
          const index = startIndex + vi;
          return (
            <div
              key={keyExtractor(item)}
              className={`grid items-center px-4 md:px-6 border-b border-pt-forest/5 transition-colors ${
                onRowClick ? 'cursor-pointer hover:bg-pt-cream/40' : 'hover:bg-pt-cream/20'
              } ${rowClassName?.(item, index) || ''}`}
              style={{
                gridTemplateColumns: gridTemplate,
                height: rowHeight,
                position: 'absolute',
                top: index * rowHeight,
                left: 0,
                right: 0,
              }}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {columns.map((col) => (
                <div
                  key={col.key}
                  className={`truncate py-3 ${alignClass(col.align)} ${col.cellClassName || ''}`}
                >
                  {col.render(item, index)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Main component ---

function DataTableInner<T>({
  columns,
  data,
  keyExtractor,
  loading,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  emptyAction,
  emptyVariant,
  onRowClick,
  rowClassName,
  sortColumn,
  sortDirection,
  onSort,
  virtualized,
  rowHeight = 72,
  overscan = 5,
  className = '',
  headerClassName = '',
  stickyHeader = false,
}: DataTableProps<T>) {
  const gridTemplate = columns.map((c) => c.width || '1fr').join(' ');

  // Loading state
  if (loading) {
    return (
      <div className="py-20 flex justify-center" aria-live="polite">
        <Loader2 className="animate-spin text-pt-gold-text" size={32} />
      </div>
    );
  }

  // Empty state
  if (data.length === 0 && emptyTitle) {
    return (
      <EmptyState
        icon={emptyIcon || Loader2}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
        variant={emptyVariant}
      />
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Header */}
      <div
        className={`grid items-center px-4 md:px-6 py-3 border-b border-pt-forest/10 bg-pt-cream/30 ${
          stickyHeader ? 'sticky top-0 z-10 backdrop-blur-sm' : ''
        } ${headerClassName}`}
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {columns.map((col) => {
          const isSortable = col.sortable && onSort;
          return (
            <div
              key={col.key}
              className={`text-[10px] font-black text-pt-sage-text uppercase tracking-widest ${alignClass(col.align)} ${
                isSortable ? 'group/sort cursor-pointer select-none flex items-center gap-1' : ''
              } ${col.headerClassName || ''}`}
              onClick={isSortable ? () => onSort!(col.key) : undefined}
            >
              {col.header}
              {isSortable && (
                <SortIcon column={col.key} sortColumn={sortColumn} sortDirection={sortDirection} />
              )}
            </div>
          );
        })}
      </div>

      {/* Body */}
      {virtualized ? (
        <VirtualizedBody
          data={data}
          columns={columns}
          keyExtractor={keyExtractor}
          rowHeight={rowHeight}
          overscan={overscan}
          onRowClick={onRowClick}
          rowClassName={rowClassName}
          gridTemplate={gridTemplate}
        />
      ) : (
        <div className="divide-y divide-pt-forest/5">
          {data.map((item, index) => (
            <div
              key={keyExtractor(item)}
              className={`grid items-center px-4 md:px-6 py-3 transition-colors ${
                onRowClick ? 'cursor-pointer hover:bg-pt-cream/40' : 'hover:bg-pt-cream/20'
              } ${rowClassName?.(item, index) || ''}`}
              style={{ gridTemplateColumns: gridTemplate }}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {columns.map((col) => (
                <div
                  key={col.key}
                  className={`truncate ${alignClass(col.align)} ${col.cellClassName || ''}`}
                >
                  {col.render(item, index)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const DataTable = React.memo(DataTableInner) as typeof DataTableInner;

export default DataTable;
export type { Column, DataTableProps };
